import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useApi } from '../services/api'; // Import for API calls (as per your file structure and tech docs)
import Avatar from 'boring-avatars'; // Added for premium, self-contained avatars
import { Link } from 'react-router-dom'; // Added for potential Link usage (optional)

// Define Job interface to fix TypeScript errors
interface Job {
  id: string;
  title: string;
  // Add other fields as needed, e.g., description: string;
}

// Mock data for progress, timeline, and learning (replace with API fetches)
const mockProgress = {
  applicationsSubmitted: 12,
  interviewsCompleted: 5,
  skillImprovement: '25%', // Or calculate dynamically
  careerScore: 78, // Out of 100
};

const mockTimeline = [
  { id: 1, company: 'TechCorp', position: 'Software Engineer', status: 'applied', date: 'Sep 20, 2025' },
  { id: 2, company: 'DesignHub', position: 'UI Designer', status: 'interviewed', date: 'Sep 15, 2025' },
  { id: 3, company: 'MarketLead', position: 'Marketing Specialist', status: 'offered', date: 'Sep 10, 2025' },
  { id: 4, company: 'ProdManage', position: 'Product Manager', status: 'rejected', date: 'Sep 5, 2025' },
];

const mockLearningData = [
  { name: 'Python', progress: 80 },
  { name: 'React', progress: 65 },
  { name: 'Leadership', progress: 45 },
];

const mockRecommendations = [
  { id: 1, course: 'Advanced React Patterns', provider: 'Coursera', link: '#' },
  { id: 2, course: 'Leadership Essentials', provider: 'LinkedIn Learning', link: '#' },
  { id: 3, course: 'Data Structures in Python', provider: 'Udemy', link: '#' },
];

const ApplicantDashboard: React.FC = () => {
  const api = useApi(); // FIXED: Call the hook here to get reactive api instance (token auto-attached)
  const navigate = useNavigate();
  const [progress, setProgress] = useState(mockProgress);
  const [timeline, setTimeline] = useState(mockTimeline);
  const [learningData, setLearningData] = useState(mockLearningData);
  const [recommendations, setRecommendations] = useState(mockRecommendations);
  const [userName, setUserName] = useState(''); // For personalized greeting
  const [jobs, setJobs] = useState<Job[]>([]); // Typed as Job[] to fix inference issues
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // Added for user feedback on fetch errors

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user name from auth endpoint
        const userResponse = await api.get('/auth/me');
        setUserName(userResponse.data.preferredName || userResponse.data.email.split('@')[0] || 'Applicant'); // Prefer preferredName, fallback to email part

        // Fetch real data (expand with your endpoints, e.g., based on Application/Assessment models)
        // const progressResponse = await api.get('/applicant/progress');
        // setProgress(progressResponse.data);
        // Similarly for timeline, learningData, recommendations

        // Added fetch for jobs (list endpoint; if 500 occurs, backend logs will show Prisma issue—ensure jobs exist or handle empty)
        const jobsResponse = await api.get('/public-jobs');
        setJobs(jobsResponse.data);
        setErrorMessage(null); // Clear error on success
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setUserName('Applicant'); // Fallback
        setErrorMessage('Failed to load jobs. Please try again or check your connection.'); // User-friendly feedback
      }
    };
    fetchData();
  }, [api]); // FIXED: Add api to deps for reactivity

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
  };

  return (
    <div className="flex min-h-screen bg-surface-page text-txt-primary">
      {/* Sidebar Navigation - Sophisticated, career-focused with hover states */}
      <aside className="w-64 bg-surface-card p-6 flex flex-col gap-4">
        <h1 className="text-2xl font-bold mb-6">Intellerview</h1>
        <button onClick={() => navigate('/applicant/dashboard')} className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium">
          Dashboard
        </button>
        <button onClick={() => navigate('/applicant/applications')} className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium">
          My Applications
        </button>
        <button onClick={() => navigate('/applicant/learning')} className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium">
          Learning Paths
        </button>
        <button onClick={() => navigate('/applicant/reports')} className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium">
          Feedback Reports
        </button>
        <button onClick={() => navigate('/applicant/settings')} className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium">
          Settings
        </button>
      </aside>

      {/* Main Content - Flexible, with padding and sections */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Header with Greeting - Personalized and inspiring */}
        <header className="mb-8 flex justify-between items-center">
          <h2 className="text-3xl font-semibold">Good {getGreeting()}, {userName}</h2>
          <p className="text-txt-muted">Your career journey overview</p>
          <div className="flex gap-4">
            <button onClick={() => navigate('/applicant/jobs')} className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors">
              Apply to Jobs
            </button>
            <button onClick={() => navigate('/applicant/learning')} className="bg-gray-700 hover:bg-gray-600 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors">
              Start Learning
            </button>
          </div>
        </header>

        {/* Progress Tracking - Elegant cards with motivating indicators */}
        <section className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-surface-card p-6 rounded-input">
            <h3 className="text-lg font-medium mb-2">Applications Submitted</h3>
            <p className="text-4xl font-bold">{progress.applicationsSubmitted}</p>
          </div>
          <div className="bg-surface-card p-6 rounded-input">
            <h3 className="text-lg font-medium mb-2">Interviews Completed</h3>
            <p className="text-4xl font-bold">{progress.interviewsCompleted}</p>
          </div>
          <div className="bg-surface-card p-6 rounded-input">
            <h3 className="text-lg font-medium mb-2">Skill Improvement</h3>
            <p className="text-4xl font-bold">{progress.skillImprovement}</p>
          </div>
          <div className="bg-surface-card p-6 rounded-input">
            <h3 className="text-lg font-medium mb-2">Career Score</h3>
            <p className="text-4xl font-bold">{progress.careerScore}/100</p>
          </div>
        </section>

        {/* Application Timeline - Beautiful list with logos and badges */}
        <section className="mb-8">
          <h3 className="text-2xl font-semibold mb-4">Application Timeline</h3>
          <ul className="space-y-4">
            {timeline.map((item) => (
              <li key={item.id} className="bg-surface-card p-4 rounded-input flex items-center gap-4">
                <Avatar name={item.company} variant="marble" size={40} />
                <div className="flex-1">
                  <h4 className="font-medium">{item.position}</h4>
                  <p className="text-txt-muted">{item.company}</p>
                </div>
                <span className="bg-brand-600 px-3 py-1 rounded-full text-sm font-medium">
                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </span>
                <p className="text-txt-muted">{item.date}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Learning Journey - Motivating chart and recommendations */}
        <section className="grid grid-cols-2 gap-6 mb-8">
          <div className="bg-surface-card p-6 rounded-input">
            <h3 className="text-2xl font-semibold mb-4">Learning Journey</h3>
            <h4 className="text-lg font-medium mb-2">Skill Progress</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={learningData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="progress" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-surface-card p-6 rounded-input">
            <h4 className="text-lg font-medium mb-2">Recommended Courses</h4>
            <ul className="space-y-2">
              {recommendations.map((rec) => (
                <li key={rec.id} className="flex justify-between items-center">
                  <span>{rec.course} ({rec.provider})</span>
                  <a href={rec.link} className="text-brand-600 hover:text-brand-700 text-sm">Start Now</a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Available Jobs - Added section for job list */}
        <section>
          <h3 className="text-2xl font-semibold mb-4">Available Jobs</h3>
          {errorMessage && <p className="text-red-400 mb-4">{errorMessage}</p>} {/* Added user feedback */}
          {jobs.length > 0 ? (
            <ul className="space-y-4">
              {jobs.map((job) => (
                <li key={job.id} className="bg-surface-card p-4 rounded-input flex items-center justify-between">
                  <h4 className="font-medium">{job.title}</h4>
                  <button onClick={() => navigate(`/jobs/${job.id}/apply`)} className="text-brand-600 hover:text-brand-700 text-sm">
                    Apply
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No jobs available.</p>
          )}
        </section>
      </main>
    </div>
  );
};

export default ApplicantDashboard;